import { Tooltip, Typography, Box, Stack } from '@mui/material';

import { useCallback, useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { getRegulationInventoryData } from 'common/api';
import { ReactComponent as FilterIcon } from 'common/assets/images/Filter.svg';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setAdvFilterDrawerState } from 'common/store/reducer/simulationReducer';
import {
	getUniqueValuesFromCommaSeperatedString,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { IRegulationInventoryList } from 'features/RegulationInventory/api/type-def';
import { handleSpecialCharacters } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import styles from './RegulationTable.module.scss';

const { simulationArena, generate } = rrcpDashboardRoutes;
const { OK, NOT_FOUND } = HTTP_STATUS_CODES;

interface IRegulationTable {
	type: string;
	appliedfilterKeys?: any;
}
const RegulationTable = (props: IRegulationTable) => {
	const { type, appliedfilterKeys = [] } = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [toastMessage, setToastMessageLocal] = useState('');
	const [regTableData, setRegTableData] = useState<IRegulationInventoryList[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const [totalPagesCount, setTotalPagesCount] = useState(0);

	// parameters for search text
	const regulationTableSearchText = useSelector(
		(state: RootState) => state.simulationArena.regulationTableSearchText,
	);

	const { tableQueryObj, updateTableQuery, queryFlag, updateQueryFlag } =
		useTableQuery({ id: 'simulationRegulationTable' });
	const defaultControlTableQueryParams = {
		pageNumber: 0,
		pageSize: 10,
	};
	const fetchRegulationTableData = async (data: any) => {
		if (appliedfilterKeys !== '') {
			setIsLoading(true);
			const apiData = {
				...data,
			};

			const updatedSearchString = handleSpecialCharacters(
				regulationTableSearchText,
			);
			const paramsObj = {
				searchText: updatedSearchString,
				index: `reginv-theme-index-${process.env.REACT_APP_ENVIRONMENT}`,
				filter: appliedfilterKeys,
			};
			const resp = await getRegulationInventoryData(paramsObj, apiData);
			if (resp?.statusCode === OK) {
				const respData = resp?.responseBody;
				const tempTableData =
					respData?.content && respData?.content?.length
						? respData?.content
						: [];
				setRegTableData(tempTableData);
				setTotalRecordCount(respData?.totalCount || 0);
				setTotalPagesCount(respData?.totalPages || 0);
				setIsLoading(false);
			} else if (resp?.statusCode === NOT_FOUND) {
				setIsToastOpen(true);
				setRegTableData([]);
				setToastMessageLocal('No Data Found');
				setIsLoading(false);
			} else {
				setIsToastOpen(true);
				setRegTableData([]);
				setToastMessageLocal(APP_GENERAL_ERROR_MSG);
				setIsLoading(false);
			}
		}
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			fetchRegulationTableData(data);
		}
	};

	const handleSortChange = (data: any) => {
		fetchRegulationTableData(data);
	};

	const reloadControlTableData = () => {
		fetchRegulationTableData(defaultControlTableQueryParams);
		updateTableQuery(defaultControlTableQueryParams);
	};

	const navigationToThemeDetail = (sectionId: string) => {
		navigate(
			urlWithTenant(`/${simulationArena}/${type}/${generate}/${sectionId}`),
		);
	};

	const columns: readonly Column[] = [
		{
			id: 'themeName',
			label: 'Theme',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-icon-wrpper'],
			render: (params: IRegulationInventoryList) => (
				<Typography className={styles['text-section-common']}>
					<Typography className={styles['theme-data']}>
						<Tooltip
							title={getUniqueValuesFromCommaSeperatedString(params.themeName)}
							placement="bottom-start"
						>
							<span>
								{getUniqueValuesFromCommaSeperatedString(params.themeName)}
							</span>
						</Tooltip>
					</Typography>
				</Typography>
			),
		},
		{
			id: 'sectionName',
			label: 'Citation',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrapper'],
			render: ({ sectionName, sectionId }: IRegulationInventoryList) => (
				<Typography
					className={styles['text-section']}
					data-testid="section-name"
					onClick={() => navigationToThemeDetail(sectionId)}
				>
					<Tooltip title={sectionName} placement="bottom-start">
						<span>{sectionName}</span>
					</Tooltip>
				</Typography>
			),
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			disablePadding: false,
			isSortable: true,
		},
	];
	useEffect(() => {
		setRegTableData([]);
		if (queryFlag) {
			fetchRegulationTableData(tableQueryObj);
			updateQueryFlag(false);
		} else {
			reloadControlTableData();
		}
	}, [regulationTableSearchText, appliedfilterKeys]);

	return (
		<>
			<Stack>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography
						className={styles['search-table-record-num']}
					>{`${totalRecordsCount.toLocaleString()} Results`}</Typography>
					<Stack gap={1} direction="row">
						<Box
							className={
								appliedfilterKeys.length === 0
									? styles['btn-filter-no-filters']
									: styles['btn-filter-selected']
							}
							onClick={() => dispatch(setAdvFilterDrawerState(true))}
							data-testid="advanced-filter-icon"
						>
							<FilterIcon />
						</Box>
					</Stack>
				</Stack>
				<Box className={styles['table-top-padding']}>
					<TableComponent
						defaultQuery={tableQueryObj}
						rows={regTableData}
						columns={columns}
						totalItems={totalRecordsCount}
						defaultSortKey=""
						onPageHandler={handlePageChange}
						isLoading={isLoading}
						tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
						totalPagesCount={totalPagesCount}
						onSortHandler={handleSortChange}
						enableHeaderColor
						isRenderPagination
					/>
				</Box>
			</Stack>
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default RegulationTable;
